// RoleDetailsDrawer — the edit-details SlideOver (name · description · inactivity
// lock) over the shared roleDetailsSchema, so it can't drift from the create
// wizard's Details step (ADR-038 drawer for a single-section edit). Real PATCH via
// useUpdateRole — the three scalars whole; on success the drawer closes and the
// ['roles', projectId] invalidate refreshes the detail + peek. The edit rides the
// row stamp (ADR-084): the shell seeds from the role as it is at each open, the
// PATCH carries the stamp, and a stale save shows the line with Load the latest.
// Scalars only — capabilities are edited on the focused capability route, so this
// never touches permissions/managed_roles.

import { useQueryClient } from '@tanstack/react-query';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DrawerForm } from '../../components/patterns/forms/DrawerForm.js';
import { readLatest } from '../../components/patterns/forms/readLatest.js';
import { useFormShell } from '../../components/patterns/forms/useFormShell.js';
import { Input } from '../../components/ui/Input.js';
import { Textarea } from '../../components/ui/Textarea.js';
import { Label } from '../../components/ui/Label.js';
import { Picker } from '../../components/patterns/Picker.js';
import { useUpdateRole } from '../../hooks/useRoleMutations.js';
import { fetchRole, rolesKeys } from '../../hooks/useRoles.js';
import type { RoleDetail } from '@ems-app/schemas/roles/responses';
import { roleDetailsSchema, type RoleDetailsValues, LOCK_MINUTES } from './roleForm.js';
import { lockMinutes, lockValue, useLockSource } from './roleDisplay.js';

export interface RoleDetailsDrawerProps {
  projectId: string;
  role: RoleDetail;
  open: boolean;
  onClose: () => void;
}

export function RoleDetailsDrawer({ projectId, role, open, onClose }: RoleDetailsDrawerProps) {
  const { t } = useTranslation('roles');
  const qc = useQueryClient();
  const update = useUpdateRole(projectId);
  const lock = useLockSource(LOCK_MINUTES, role.inactivity_timeout_min);

  const shell = useFormShell<RoleDetailsValues, RoleDetail>({
    schema: roleDetailsSchema,
    row: role,
    values: (r) => ({ name: r?.name ?? '', description: r?.description ?? '', inactivity_timeout_min: r?.inactivity_timeout_min ?? null }),
    // The fresh role: a quiet read (a role gone meanwhile is said in the form), into the page's entry once it answers.
    reload: () => readLatest(qc, rolesKeys.detail(projectId, role.id), () => fetchRole(projectId, role.id, true)),
    onSubmit: async (v, { stamp }) => {
      await update.mutateAsync({
        roleId: role.id,
        // '' → null (never stricter than the backend; description is nullable).
        body: { row_version: stamp, name: v.name, description: v.description ? v.description : null, inactivity_timeout_min: v.inactivity_timeout_min },
      });
    },
    onSuccess: onClose,
  });
  const e = shell.form.formState.errors;

  return (
    <DrawerForm shell={shell} open={open} onClose={onClose} title={t('actions.editRole')} description={role.name} submitLabel={t('drawer.submit')} autoFocusField="name">
      <div>
        <Label htmlFor="rd-name" required>
          {t('drawer.name')}
        </Label>
        <Input id="rd-name" {...shell.form.register('name')} error={e.name ? ' ' : undefined} />
      </div>
      <div>
        <Label htmlFor="rd-desc">{t('drawer.description')}</Label>
        <Textarea id="rd-desc" rows={3} {...shell.form.register('description')} error={e.description ? ' ' : undefined} />
      </div>
      <Controller
        control={shell.form.control}
        name="inactivity_timeout_min"
        render={({ field }) => (
          <Picker source={lock} label={t('drawer.lock')} value={lockValue(field.value)} onChange={(v) => field.onChange(lockMinutes(v))} />
        )}
      />
    </DrawerForm>
  );
}
